import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import PageMeta from "@/components/PageMeta";

/**
 * Public, no-login FAQ page.
 *
 * The primary audience here is BOTH humans (potential customers
 * evaluating Slides by Wokelo) and answer engines (Perplexity, ChatGPT
 * Search, Google AI Overviews) — which is why this page also emits a
 * `FAQPage` JSON-LD block.
 *
 * Why FAQ + JSON-LD matters:
 *   - Answer engines look for structured Q/A pairs (FAQPage schema) and
 *     surface them DIRECTLY in their answer with attribution back to
 *     this page.
 *   - The same schema enables Google "People also ask" rich results
 *     for the matching queries.
 *
 * Each Q/A pair is duplicated between the rendered DOM and the JSON-LD
 * block. Keep them in sync — when adding a new FAQ, update both arrays
 * below in this file.
 *
 * Content sourced from the live product behavior (pricing config,
 * generation flow, file-upload limits, data-privacy posture) as of
 * Feb 2026. Edit freely.
 */

// Source of truth for both DOM rendering AND the JSON-LD schema.
// `answer` accepts plain text (rendered as a paragraph) or arrays of
// strings (rendered as bullet points).
const FAQS = [
  {
    q: "What is Slides by Wokelo?",
    a: "Slides by Wokelo is an AI-powered presentation generator that produces consulting-grade PowerPoint decks in minutes. It is built for management consultants, investment bankers, private equity associates, corporate strategy teams, and M&A deal teams who need polished, defensible slides without the overhead of a design team.",
  },
  {
    q: "How does Slides by Wokelo work?",
    a: "You provide a topic, upload source documents (PDFs, Word, Excel, PowerPoint), or paste raw research, and the AI generates a fully formatted .pptx file. You can produce single slides or multi-slide decks of up to 25 slides on most plans (50 on Enterprise). Downloads are real PowerPoint files that open natively in Microsoft PowerPoint and Google Slides.",
  },
  {
    q: "What plans and pricing are available?",
    a: [
      "Trial — 50 one-time credits, 1 PDF upload, no credit card required.",
      "Starter — 100 credits per month, PDF uploads, email support, personalization.",
      "Pro — 500 credits per month, all file formats, 20MB / 100 page limit, premium data add-on, custom templates, email and chat support.",
      "Enterprise — custom credits, no file size limits, priority capacity, custom organization templates, API access, SSO / SAML, on-prem or VPC deployment, private LLM (100% local AI).",
    ],
  },
  {
    q: "Is my content used to train AI models?",
    a: "No. Across every plan — Trial, Starter, Pro, and Enterprise — your content is never used to train any AI model. This is a hard guarantee, not a setting you have to opt out of.",
  },
  {
    q: "What file formats can I upload?",
    a: "Starter accepts PDF only. Pro accepts PDF, Word (.doc, .docx), Excel (.xls, .xlsx), PowerPoint (.ppt, .pptx), and common image formats (.png, .jpg, .jpeg, .webp). Enterprise accepts all of the above plus CSV, TXT, Markdown, and HEIC/GIF. Trial is capped at one PDF up to 10MB.",
  },
  {
    q: "How long does generation take?",
    a: "Single slides typically take 2-4 minutes. Multi-slide decks scale roughly with slide count — about 3 minutes per slide on average. Enterprise customers get priority capacity, which avoids queueing during peak load.",
  },
  {
    q: "Can I use my own templates and brand colors?",
    a: "Yes. Pro lets you upload custom templates. Enterprise extends this with organization-wide templates managed centrally for your team. The Personalization feature on Starter lets you tune layout, color, and tone preferences per-user.",
  },
  {
    q: "Do you offer SSO, on-prem, or private LLM deployments?",
    a: "Yes — these are Enterprise-tier features. SSO / SAML, on-prem or VPC deployment, and private LLM (a fully local AI model running inside your environment with no data leaving your network) are all available. Contact us at sid@wokelo.ai for a demo.",
  },
  {
    q: "How do credits work?",
    a: "Each generation consumes credits roughly proportional to its complexity — more research and more slides cost more. Unused credits on Starter and Pro roll over up to 2x your monthly amount. Trial credits do not roll over.",
  },
  {
    q: "Can I share decks with my team?",
    a: "Yes. Every generated deck or slide has a public-share option that produces an email-gated link viewable by anyone — perfect for sharing with clients or colleagues who do not have a Wokelo account. The downloaded .pptx file itself has no watermark.",
  },
  {
    q: "What data sources do you connect to?",
    a: "The base product synthesizes from the documents you upload and from Wokelo's Research APIs. The Pro and Enterprise tiers include a Premium data add-on with access to S&P Capital IQ, Crunchbase, LinkedIn, SimilarWeb, and several other alternative datasets covering patents, reviews, podcast transcripts and more.",
  },
  {
    q: "Where is Wokelo based and who built it?",
    a: "Slides by Wokelo is built by Wokelo AI. Visit www.wokelo.ai or reach out at sid@wokelo.ai for enterprise inquiries.",
  },
];

// Build the JSON-LD payload from FAQS at module load — single source of truth.
const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": Array.isArray(a) ? a.join(" ") : a,
    },
  })),
};

export default function FAQPage() {
  const navigate = useNavigate();
  // Accordion: only one item open at a time keeps the page short on
  // mobile. Open the first by default so AEO crawlers see at least
  // one answer in the unscrolled viewport without JS.
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="min-h-screen bg-[#f4f4f4] font-['Figtree']" data-testid="faq-page">
      <PageMeta
        title="FAQ · Slides by Wokelo — AI for consulting-grade PowerPoint decks"
        description="Frequently asked questions about Slides by Wokelo: how it works, pricing, supported file formats, data privacy, SSO, custom templates, and more."
      />

      {/* JSON-LD for AEO / answer engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }}
      />

      {/* Simple top bar with brand mark + login CTA */}
      <header className="w-full px-6 sm:px-12 py-5 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          data-testid="brand-home-link"
        >
          <img src="/landing/wokelo_logo.svg" alt="Wokelo" className="w-[110px] h-auto" />
        </button>
        <button
          onClick={() => navigate("/login")}
          className="text-[14px] font-semibold text-teal-600 hover:text-teal-700"
          data-testid="header-login-link"
        >
          Log in →
        </button>
      </header>

      <main className="max-w-[760px] mx-auto px-6 py-12">
        <h1 className="text-[40px] leading-tight font-extrabold text-gray-900 tracking-tight mb-3" data-testid="faq-heading">
          Frequently asked questions
        </h1>
        <p className="text-[15px] text-gray-500 mb-10">
          Everything about Slides by Wokelo — how it works, pricing, file
          support, data privacy, and enterprise deployment.
        </p>

        <div className="space-y-3">
          {FAQS.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className="w-full flex items-center justify-between text-left px-6 py-5 hover:bg-gray-50 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-gray-900 text-[15px] pr-4">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 text-[14px] text-gray-600 leading-relaxed">
                    {Array.isArray(item.a) ? (
                      <ul className="list-disc pl-5 space-y-1.5">
                        {item.a.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{item.a}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-[14px] text-gray-500 mb-3">Still have questions?</p>
          <a
            href="mailto:sid@wokelo.ai?subject=Slides%20by%20Wokelo%20%E2%80%94%20Question"
            className="text-[14px] font-semibold text-teal-600 hover:text-teal-700"
            data-testid="contact-email-link"
          >
            Email sid@wokelo.ai →
          </a>
        </div>
      </main>

      <footer className="text-center py-10 px-6 text-[12px] text-gray-400">
        <a href="https://www.wokelo.ai/legal?tab=privacy" className="hover:text-gray-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Privacy</a>
        {" · "}
        <a href="https://www.wokelo.ai/legal" className="hover:text-gray-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Terms</a>
        {" · "}
        <span>© 2026 Wokelo AI</span>
      </footer>
    </div>
  );
}
